import {githubHeaders, issueUrl, labelUrl} from './urls';

import type {GithubComment, GithubIssue} from './githubTypes';

const OK = 200;
const CREATED = 201;

/**
 * Check if label exists.
 *
 * @param label The label.
 * @returns True if label exists, false otherwise.
 */
export const checkIfLabelExists = async (label: string) => {
    const response = await fetch(`${labelUrl}/${label}%20:art:`, {headers: githubHeaders});

    if (response.status === OK) {
        return true;
    }

    return false;
};

/**
 * Create label.
 *
 * @param label The label.
 * @returns The status.
 */
export const createLabel = async (label: string) => {
    const response = await fetch(labelUrl, {
        body: JSON.stringify({
            color: 'ff4785',
            description: 'An label to match the story ID in storybook to the issue in GitHub.',
            name: `${label} :art:`
        }),
        headers: githubHeaders,
        method: 'POST'
    });

    return response.status;
};

/**
 * Create issue.
 *
 * @param title    The title.
 * @param body     The body.
 * @param storyUrl The story URL.
 * @param storyId  The story ID.
 * @returns The issue.
 */
export const createIssue = async (title: string, body: string, storyUrl: string, storyId: string) => {
    // eslint-disable-next-line no-param-reassign
    body += `\n\n[Storybook Story](${storyUrl})`;

    const response = await fetch(issueUrl, {
        body: JSON.stringify({
            assignees: ['ckruppe'],
            body,
            labels: [`${storyId} :art:`],
            title
        }),
        headers: githubHeaders,
        method: 'POST'
    });

    if (response.status === CREATED) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return response.json();
    }

    return null;
};

/**
 * Update Issue.
 *
 * @param issueNumber The issue number.
 * @returns The issue.
 */
export const finishIssue = async (issueNumber: number) => {
    const response = await fetch(`${issueUrl}/${issueNumber}`, {
        body: JSON.stringify({
            assignees: [],
            labels: ['completed :art:'],
            state: 'closed',
            // eslint-disable-next-line camelcase
            state_reason: 'completed'
        }),
        headers: githubHeaders,
        method: 'PATCH'
    });

    if (response.status === OK) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return response.json();
    }

    return null;
};

/**
 * Get issues.
 *
 * @param storyId The story ID.
 * @returns The issues.
 */
export const getIssues = async (storyId: string) => {
    const response = await fetch(`${issueUrl}?state=all&labels=${storyId}%20:art:&rand=${Date.now()}`, {headers: githubHeaders});

    if (response.status === OK) {
        return response.json() as Promise<GithubIssue[]>;
    }

    return null;
};

/**
 * Update Issue.
 *
 * @param issueNumber The issue number.
 * @returns The issue.
 */
export const setIssueOpen = async (issueNumber: number) => {
    const response = await fetch(`${issueUrl}/${issueNumber}`, {
        body: JSON.stringify({
            assignees: ['ckruppe'],
            state: 'open'
        }),
        headers: githubHeaders,
        method: 'PATCH'
    });

    if (response.status === OK) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return response.json();
    }

    return null;
};

/**
 * Get comments.
 *
 * @param commentUrl The comment URL.
 * @returns The comments.
 */
export const getComments = async (commentUrl: string) => {
    const response = await fetch(`${commentUrl}?rand=${Date.now()}`, {headers: githubHeaders});

    if (response.status === OK) {
        return response.json() as Promise<GithubComment[]>;
    }

    return null;
};

/**
 * Get comments.
 *
 * @param issueNumber The issue number.
 * @param comment     The comment.
 * @returns The comments.
 */
export const createComment = async (issueNumber: number, comment: string) => {
    const response = await fetch(`${issueUrl}/${issueNumber}/comments`, {
        body: JSON.stringify({body: comment}),
        headers: githubHeaders,
        method: 'POST'
    });

    if (response.status === CREATED) {
        return response.json() as Promise<GithubComment[]>;
    }

    return null;
};

export interface TransformedIssue {
    avatar: string;
    body: string;
    commentNumber: number;
    comments: {
        avatar?: string;
        body: string;
        date?: Date;
        id: string;
        type?: 'error';
    }[];
    createdAt: Date;
    id: string;
    number: number;
    state: 'closed' | 'open';
    title: string;
    updatedAt: Date;
}

/**
 * Transform issues.
 *
 * @param issues The issues.
 * @returns The transformed issues.
 */
export const transformIssues = async (issues: GithubIssue[]) => {
    const transformedIssues = await Promise.allSettled(issues.map(async issue => {
        let comments: GithubComment[] | null = [];
        let error = null;

        if (issue.comments > 0) {
            comments = await getComments(issue.comments_url);

            if (comments === null) {
                error = [{
                    body: 'Could not load comments.',
                    id: 'error',
                    type: 'error'
                }];
            }
        }

        return {
            avatar: issue.user.avatar_url,
            body: issue.body,
            commentNumber: issue.comments,
            comments: comments?.map(comment => ({
                avatar: comment.user.avatar_url,
                body: comment.body,
                date: new Date(comment.created_at),
                id: comment.node_id
            })) ?? error,
            createdAt: new Date(issue.created_at),
            id: issue.node_id,
            number: issue.number,
            state: issue.state,
            title: issue.title,
            updatedAt: new Date(issue.updated_at)
        } as TransformedIssue;
    }));

    return (transformedIssues
        .map(issue => (issue.status === 'fulfilled' ? issue.value : null))
        .filter(issue => issue !== null))
        // @ts-expect-error
        .sort((a, b) => b.updatedAt - a.updatedAt);
};

/**
 * Format date.
 *
 * @param date The date.
 * @returns The formatted date.
 */
export const format = (date: Date) => new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    month: '2-digit',
    year: 'numeric'
}).format(date);